import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, Edit, Trash2, Mail, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsers = () => {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editAdmin, setEditAdmin] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({ full_name: '', email: '', is_active: true });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setAdmins(res.data);
    } catch { toast.error('Failed to fetch admins'); }
    finally { setLoading(false); }
  };

  const handleEdit = (admin) => {
    setEditAdmin(admin);
    setEditFormData({ full_name: admin.full_name, email: admin.email, is_active: admin.is_active });
    setShowEditModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(`${API}/admin/users/${editAdmin.id}`, editFormData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Admin updated!');
      setShowEditModal(false);
      fetchAdmins();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (adminId, username) => {
    if (!window.confirm(`Delete admin "${username}"?`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/users/${adminId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Admin deleted');
      fetchAdmins();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button data-testid="back-to-dashboard" variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')} className="text-white hover:bg-slate-800"><ArrowLeft className="w-4 h-4" /></Button>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Admin Users</h1>
          </div>
          <Button data-testid="add-admin-button" onClick={() => navigate('/admin/register')} className="bg-orange-500 hover:bg-orange-600 rounded-full" size="sm"><UserPlus className="w-4 h-4 mr-2" />Add Admin</Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <Card className="border-slate-100 shadow-sm">
          <CardHeader><CardTitle>All Admin Users</CardTitle></CardHeader>
          <CardContent>
            {loading ? <div className="text-center py-8">Loading...</div> : admins.length === 0 ? <div className="text-center py-8 text-slate-600">No admin users</div> : (
              <div className="space-y-3">
                {admins.map((admin) => (
                  <div key={admin.id} data-testid={`admin-${admin.id}`} className="border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                    <div className="space-y-1 flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-semibold text-lg">{admin.full_name}</h3>
                        <Badge className={admin.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>{admin.is_active ? 'Active' : 'Inactive'}</Badge>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-slate-600">
                        <span className="flex items-center"><User className="w-4 h-4 mr-1" />{admin.username}</span>
                        <span className="flex items-center"><Mail className="w-4 h-4 mr-1" />{admin.email}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button data-testid={`edit-${admin.id}`} onClick={() => handleEdit(admin)} variant="outline" size="sm" className="rounded-full"><Edit className="w-4 h-4" /></Button>
                      <Button data-testid={`delete-${admin.id}`} onClick={() => handleDelete(admin.id, admin.username)} variant="destructive" size="sm" className="rounded-full"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Admin</DialogTitle><DialogDescription>Update admin details</DialogDescription></DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4">
            <div className="space-y-2"><Label>Full Name</Label><Input data-testid="edit-fullname-input" value={editFormData.full_name} onChange={(e) => setEditFormData({...editFormData, full_name: e.target.value})} className="h-12" required /></div>
            <div className="space-y-2"><Label>Email</Label><Input data-testid="edit-email-input" type="email" value={editFormData.email} onChange={(e) => setEditFormData({...editFormData, email: e.target.value})} className="h-12" required /></div>
            <div className="flex items-center justify-between"><Label>Active Status</Label><Switch data-testid="edit-active-switch" checked={editFormData.is_active} onCheckedChange={(c) => setEditFormData({...editFormData, is_active: c})} /></div>
            <div className="flex space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} className="flex-1 rounded-full">Cancel</Button>
              <Button type="submit" data-testid="save-admin-button" className="flex-1 bg-slate-900 hover:bg-slate-800 rounded-full">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
